<template>
  <filter-wrapper
    class="ndfe-search-filter--make v-relative"
    type="expanded"
    v-bind="$props"
    label="Make"
    :id="`ndfe-option_make`"
  >
    <component
      :is="componentName"
      :attach="`#ndfe-option_make`"
      :value="computedValue"
      :clearable="!$vuetify.breakpoint.smAndDown"
      flat
      outlined
      :items="computedItems"
      @change="handleChange"
      :clear-icon="mdiCached"
      multiple
      :placeholder="$t('CORE.MAKES')"
      hide-details
      solo
      @click:clear="clear"
    >
      <template slot="selection" slot-scope="{ item, index }">
        <span class="me-2 d-flex align-center" v-if="index === 0">
          <span>{{ item.text }}</span>
          <span class="grey--text body-2 ndfe-other-selection" v-if="computedValue.length > 1">
            &nbsp;(+{{ computedValue.length - 1 }} {{ $t('CORE.OTHERS') }})
          </span>
        </span>
      </template>

      <template slot="prepend-item">
        <core-list-item ripple @click="clear">
          <div class="d-flex align-center">
            <span>{{ $t('MAKES.ALL') }}</span>
            <span class="grey--text caption ps-1">({{ totalCount }})</span>
          </div>
        </core-list-item>
        <core-divider class="mt-2" />
      </template>

      <template slot="item" slot-scope="{ item }" :disabled="item.disabled">
        <div class="d-flex align-center">
          <span>{{ item.text }}</span>
          <span class="grey--text caption ps-1">({{ item.count }})</span>
        </div>
      </template>
    </component>
  </filter-wrapper>
</template>

<script>
import { mdiCached } from '@mdi/js';
import { isEmpty, cloneDeep } from '@/helpers/utilities';
import filterMixin from '@/mixins/search/filter';
import makeFilter from '@/mixins/search/make-model-trim';
import {
  modelFilterName,
  manufacturerFilterName,
  variantFilterName,
} from '@/helpers/search-client/filterNames';

export default {
  name: 'SearchFilterMakes',
  isCustomElement: true,
  mixins: [filterMixin, makeFilter],
  components: {
    FilterWrapper: () => import(/* webpackChunkName: "FilterWrapper" */ './Wrapper.vue').then((d) => d.default),
    CoreSelect: () => import(/* webpackChunkName: "CoreSelect" */ '../../core/Select.vue').then((d) => d.default),
    CoreAutocomplete: () => import(/* webpackChunkName: "CoreAutocomplete" */ '../../core/Autocomplete.vue').then((d) => d.default),
  },
  data() {
    return {
      filterName: manufacturerFilterName,
      tracking: {
        fieldName: manufacturerFilterName,
        name: 'filter_make',
        ui: 'autocomplete',
      },
      mdiCached,
    };
  },
  props: {
    breakpoints: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    computedItems(newVal) {
      // if only one item in the makes filter
      // then select that one
      const values = [newVal[0]?.value];
      if (newVal.length === 1) this.handleChange(values);
    },
  },
  computed: {
    isOnSearchListing() {
      return this.$attrs.isSearchListingPage;
    },
    componentName() {
      return this.$vuetify.breakpoint.smAndDown ? 'core-select' : 'core-select';
    },
    totalCount() {
      if (this.computedItems.length === 0) return 0;
      return this.computedItems.reduce((sum, item) => sum + Number(item.count), 0);
    },
    computedItems() {
      if (isEmpty(this.filterValues) || !this.filterValues.items) return [];
      const items = this.filterValues.items.map((n) => ({
        text: n.label,
        value: n.label,
        count: n.count,
        disabled: n.count === 0,
      }));
      if (this.isMobile) return [{ text: this.$t('CORE.ALL'), value: -1 }, ...items];
      return items;
    },
    computedValue() {
      if (this.$props.value) return this.$props.value;
      const values = this.searchCriteria[this.filterName];
      if (!values) return [];
      return values;
    },
  },
  methods: {
    clear() {
      if (!this.isOnSearchListing) {
        const searchCriteria = cloneDeep(this.searchCriteria);
        searchCriteria[this.filterName] = ['All'];
        searchCriteria[modelFilterName] = ['All'];
        searchCriteria[variantFilterName] = ['All'];

        this.commit('search/setValue', {
          key: 'searchCriteria',
          value: searchCriteria,
        });
      } else {
        this.removeSearchCriteria();
        this.removeSearchCriteria(modelFilterName);
        this.removeSearchCriteria(variantFilterName);
      }

      this.dispatch('app/TRACKING_EVENT', {
        event: 'SEARCH_INTERACTION',
        data: {
          ...this.tracking,
          isRemoved: true,
        },
      });
    },
    handleChange(values) {
      let newValues = [...values];
      if (values.length > 1) {
        newValues = newValues.filter((v) => v !== 'All');
      }

      // if value is empty run clear function
      if (newValues.length === 0 || newValues.includes(-1)) return this.clear();
      // remove any models that have no manufacturer selected
      this.removeSelectedModels(newValues);
      // remove any variants that have no model selected
      this.removeSelectedVariants(this.searchCriteria[modelFilterName]);
      this.updateSearchCriteria(newValues);
      return this.dispatch('app/TRACKING_EVENT', {
        event: 'SEARCH_INTERACTION',
        data: {
          ...this.tracking,
        },
      });
    },
  },
};
</script>

<style lang="scss">
#ndfe-option_make {
  .v-menu__content {
    top: unset !important;
    left: unset !important;
  }
}
</style>
